import { jsx } from 'react/jsx-runtime';
import { createContext, useContext } from 'react';
import { getHeaderCorrelationId, objectToJsonMapper, successfulResponseToObject, reactQueryRetry, isFunctionalError } from '@axa-fr-axion/restful';
import { useQuery } from '@tanstack/react-query';
import { w as withSelectedAdvisor, a as withDevice, b as buildFolderUrl, c as buildFindAdvisorsUrl, d as buildRequestContactUrl, e as buildIdentifyUrl } from './Person.utils-bb402851.mjs';
import '@axa-fr-axion/utils';

const userJourneyServicesContext = createContext(void 0);

const isTestMode = () => process.env.NODE_ENV === "test";
const getReactQueryRetryFnc = (fnc) => {
  if (isTestMode()) {
    return false;
  }
  return fnc;
};

const UserJourneyServicesProvider = ({ config, children }) => {
  if (!config) {
    throw new Error("[UserJourneyServicesProvider] The config must be provided to the user journey services to work as expected.");
  }
  return /* @__PURE__ */ jsx(userJourneyServicesContext.Provider, { value: config, children });
};

const useUserJourneyServicesContext = () => {
  const context = useContext(userJourneyServicesContext);
  if (!!context) {
    return context;
  }
  throw new Error('[useUserJourneyServicesContext] To use the "useUserJourneyServicesContext" hook, you must use the "UserJourneyServicesProvider" component.');
};

const postFolder = async (config, data) => {
  const body = {
    origine: withSelectedAdvisor(data?.origine),
    proprietes: withDevice(data?.proprietes)
  };
  return fetch(
    buildFolderUrl(config),
    {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        ...getHeaderCorrelationId(config.headersCorrelationName)
      },
      body: objectToJsonMapper(body)
    }
  ).then(successfulResponseToObject);
};
const useFolder = (origine, options) => {
  const config = useUserJourneyServicesContext();
  return useQuery(
    ["folder", origine],
    () => postFolder(config, origine),
    {
      enabled: options?.isEnabled,
      retry: getReactQueryRetryFnc(reactQueryRetry),
      refetchOnWindowFocus: false,
      onSuccess: options?.onSuccess,
      onError: options?.onTechnicalError
    }
  );
};
const postFindAdvisors = (config, folderId, leadId, folder) => {
  return fetch(
    buildFindAdvisorsUrl(config, folderId, leadId),
    {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        ...getHeaderCorrelationId(config.headersCorrelationName)
      },
      body: objectToJsonMapper(folder)
    }
  ).then(successfulResponseToObject);
};
const useFindAdvisors = (folderId, leadId, folder, options) => {
  const config = useUserJourneyServicesContext();
  return useQuery(
    ["findAdvisors", folderId, leadId, folder],
    () => postFindAdvisors(config, folderId, leadId, folder),
    {
      enabled: Boolean(folderId?.length && leadId?.length && folder?.personne),
      refetchOnWindowFocus: false,
      retry: getReactQueryRetryFnc(reactQueryRetry),
      onSuccess: options?.onSuccess,
      onError: async (restError) => {
        const error = restError.body && await restError.body();
        if (isFunctionalError(error)) {
          options?.onFunctionalError?.(error);
          return;
        }
        options?.onTechnicalError?.();
      }
    }
  );
};
const postRequestContact = (config, folderId, leadId, folder) => {
  return fetch(
    buildRequestContactUrl(config, folderId, leadId),
    {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        ...getHeaderCorrelationId(config.headersCorrelationName)
      },
      body: objectToJsonMapper(folder)
    }
  ).then(successfulResponseToObject);
};
const useRequestContact = (folderId, leadId, folder, options) => {
  const config = useUserJourneyServicesContext();
  return useQuery(
    ["requestContact", folderId, leadId, folder],
    () => postRequestContact(config, folderId, leadId, folder),
    {
      enabled: Boolean(folderId?.length && leadId?.length && folder),
      retry: getReactQueryRetryFnc(reactQueryRetry),
      refetchOnWindowFocus: false,
      onSuccess: options?.onSuccess,
      onError: options?.onTechnicalError
    }
  );
};

const UNKNOWN_PROSPECT = {
  profilClient: "Prospect"
};
const UNKNOWN_FOLDER = {
  lead: {
    quoteVisible: true
  },
  personne: UNKNOWN_PROSPECT
};
const postIdentify = async (config, folder) => {
  return fetch(
    buildIdentifyUrl(config),
    {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        ...getHeaderCorrelationId(config.headersCorrelationName)
      },
      body: objectToJsonMapper(folder)
    }
  ).then(successfulResponseToObject);
};
const useIdentify = (folder, options) => {
  const config = useUserJourneyServicesContext();
  const response = useQuery(
    ["identify", folder],
    () => postIdentify(config, folder),
    {
      enabled: Boolean(folder?.personne?.nom && folder?.personne?.prenom),
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onSuccess: options?.onSuccess,
      onError: async (restError) => {
        const error = restError.body && await restError.body();
        if (isFunctionalError(error)) {
          options?.onFunctionalError?.(error);
          return;
        }
        options?.onTechnicalError?.();
      },
      // no cache since it is a post behind
      cacheTime: 0,
      retry: getReactQueryRetryFnc(reactQueryRetry)
    }
  );
  return {
    ...response,
    data: response.data || UNKNOWN_FOLDER
    // Cast required to 'override' loading and error object.
    // That way we always return a default folder at least.
  };
};

export { UNKNOWN_FOLDER, UNKNOWN_PROSPECT, UserJourneyServicesProvider, buildFindAdvisorsUrl, buildFolderUrl, buildRequestContactUrl, getReactQueryRetryFnc, isTestMode, postFindAdvisors, postFolder, postIdentify, postRequestContact, useFindAdvisors, useFolder, useIdentify, useRequestContact, useUserJourneyServicesContext, userJourneyServicesContext, withDevice, withSelectedAdvisor };
